import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import ModalGerenciarGrupos, {
    MODAL_ID as MODAL_GERENCIAR_GRUPOS_ID,
} from "./ModalGerenciarGrupos";
import ModalNovoGrupo, { MODAL_ID as MODAL_NOVO_GRUPO_ID } from "./ModalNovoGrupo";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const LIST_USUARIOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListUsuarios/";
const LIST_GRUPOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListGrupos/";
const LIST_MODULOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListModulos/";
const ADD_GRUPO_URL = "/Painel/API/AccessManagement/GestaoAcessos/AddGrupoUsuario/";
const REMOVE_GRUPO_URL = "/Painel/API/AccessManagement/GestaoAcessos/RemoveGrupoUsuario/";

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const GruposCell = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    box-sizing: border-box;
`;

const GrupoBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 220px;
    background-color: #eef2ff;
    border: 1px solid #c7d2fe;
    color: #3730a3;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const RemoveGrupoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background-color: transparent;
    color: #3730a3;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    padding: 0;

    &:hover {
        background-color: rgba(55, 48, 163, 0.15);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const AddGrupoSelect = styled.select`
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 26px;
    max-width: 170px;
    border-radius: 999px;
    border: 1px dashed rgba(0, 0, 0, 0.3);
    background-color: #fff;
    color: #475569;
    padding: 0 24px 0 10px;
    box-sizing: border-box;
    font-size: 12px;
    outline: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 10px;

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function buildSearchHaystack(row) {
    if (!row) return "";

    const nomesGrupos = Array.isArray(row.grupos) ? row.grupos.map((g) => g.nome) : [];

    return [row.usuario, row.user_id, row.email, ...nomesGrupos]
        .filter((v) => v != null && String(v).trim() !== "" && String(v).trim() !== "—")
        .join(" ")
        .toUpperCase();
}

const GestaoAcessosV2 = () => {
    const api = useApi();
    const { isModalOpen } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [modulosGerenciaveis, setModulosGerenciaveis] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [savingId, setSavingId] = useState(null);
    const [q, setQ] = useState("");

    const managedModulos = useMemo(
        () => new Set(modulosGerenciaveis.filter(Boolean)),
        [modulosGerenciaveis]
    );

    const gruposAtribuiveis = useMemo(
        () =>
            managedModulos.size === 0
                ? []
                : grupos.filter((g) => managedModulos.has(g.modulo)),
        [grupos, managedModulos]
    );

    // Grupos que ainda podem ser adicionados à linha: dentro do escopo do gestor
    // e de módulos que o usuário ainda não ocupa (regra: 1 grupo por módulo)
    const getGruposDisponiveis = useCallback(
        (row) => {
            const modulosOcupados = new Set(
                (Array.isArray(row?.grupos) ? row.grupos : []).map((g) => g.modulo)
            );

            return gruposAtribuiveis.filter((g) => !modulosOcupados.has(g.modulo));
        },
        [gruposAtribuiveis]
    );

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const [usuariosRes, gruposRes, modulosRes] = await Promise.all([
                api.get(LIST_USUARIOS_URL),
                api.get(LIST_GRUPOS_URL),
                api.get(LIST_MODULOS_URL),
            ]);

            setUsuarios(Array.isArray(usuariosRes.data) ? usuariosRes.data : []);
            setGrupos(Array.isArray(gruposRes.data) ? gruposRes.data : []);
            setModulosGerenciaveis(Array.isArray(modulosRes.data) ? modulosRes.data : []);
        } catch (error) {
            console.error(error);
            setUsuarios([]);
            setGrupos([]);
            setModulosGerenciaveis([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar usuários.");
        } finally {
            setLoading(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return usuarios;

        return usuarios.filter((row) => buildSearchHaystack(row).includes(query));
    }, [usuarios, q]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const updateRowGrupos = useCallback((rowId, updater) => {
        setUsuarios((prev) =>
            prev.map((u) =>
                Number(u.id) === Number(rowId)
                    ? { ...u, grupos: updater(Array.isArray(u.grupos) ? u.grupos : []) }
                    : u
            )
        );
    }, []);

    const handleAddGrupo = useCallback(
        async (row, grupoIdRaw) => {
            if (!row?.id || savingId != null) return;

            const grupoId = grupoIdRaw === "" || grupoIdRaw == null ? null : Number(grupoIdRaw);
            if (grupoId == null) return;

            const grupo = grupos.find((g) => Number(g.id) === grupoId);
            if (!grupo) return;

            setSavingId(row.id);

            try {
                const { data } = await api.post(ADD_GRUPO_URL, {
                    user_id: row.id,
                    grupo_id: grupoId,
                });

                updateRowGrupos(row.id, (atuais) =>
                    [...atuais.filter((g) => Number(g.id) !== grupoId), {
                        id: grupoId,
                        nome: grupo.nome,
                        modulo: grupo.modulo,
                    }].sort((a, b) => String(a.nome).localeCompare(String(b.nome)))
                );
                notifySuccess(data?.message || "Grupo adicionado ao usuário.");
            } catch (error) {
                console.error(error);
                notifyError(error?.response?.data?.message || "Erro ao adicionar grupo ao usuário.");
            } finally {
                setSavingId(null);
            }
        },
        [api, grupos, notifyError, notifySuccess, savingId, updateRowGrupos]
    );

    const handleRemoveGrupo = useCallback(
        async (row, grupo) => {
            if (!row?.id || !grupo?.id || savingId != null) return;

            setSavingId(row.id);

            try {
                const { data } = await api.post(REMOVE_GRUPO_URL, {
                    user_id: row.id,
                    grupo_id: grupo.id,
                });

                updateRowGrupos(row.id, (atuais) =>
                    atuais.filter((g) => Number(g.id) !== Number(grupo.id))
                );
                notifySuccess(data?.message || "Grupo removido do usuário.");
            } catch (error) {
                console.error(error);
                notifyError(error?.response?.data?.message || "Erro ao remover grupo do usuário.");
            } finally {
                setSavingId(null);
            }
        },
        [api, notifyError, notifySuccess, savingId, updateRowGrupos]
    );

    const columns = useMemo(
        () => [
            {
                id: "usuario",
                header: "NOME",
                accessor: "usuario",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "user_id",
                header: "USUÁRIO",
                accessor: "user_id",
                sortable: true,
                width: "180px",
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "email",
                header: "E-MAIL",
                accessor: "email",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "grupos",
                header: "GRUPOS",
                accessor: (row) =>
                    (Array.isArray(row.grupos) ? row.grupos : [])
                        .map((g) => g.nome)
                        .join(", "),
                sortable: true,
                width: "320px",
                render: (_value, row) => {
                    const gruposDoUsuario = Array.isArray(row.grupos) ? row.grupos : [];
                    const disponiveis = getGruposDisponiveis(row);
                    const saving = savingId === row.id;

                    return (
                        <GruposCell onClick={(e) => e.stopPropagation()}>
                            {gruposDoUsuario.map((grupo) => {
                                const canRemove = managedModulos.has(grupo.modulo);

                                return (
                                    <GrupoBadge key={grupo.id} title={`Módulo: ${grupo.modulo}`}>
                                        <span>{grupo.nome}</span>
                                        {canRemove && (
                                            <RemoveGrupoButton
                                                type="button"
                                                disabled={saving}
                                                aria-label={`Remover grupo ${grupo.nome}`}
                                                onClick={() => handleRemoveGrupo(row, grupo)}
                                            >
                                                ×
                                            </RemoveGrupoButton>
                                        )}
                                    </GrupoBadge>
                                );
                            })}

                            {disponiveis.length > 0 && (
                                <AddGrupoSelect
                                    value=""
                                    disabled={saving}
                                    aria-label={`Adicionar grupo ao usuário ${row.usuario || row.user_id || row.id}`}
                                    onChange={(e) => handleAddGrupo(row, e.target.value)}
                                >
                                    <option value="">+ Adicionar</option>
                                    {disponiveis.map((grupo) => (
                                        <option key={grupo.id} value={String(grupo.id)}>
                                            {grupo.nome}
                                        </option>
                                    ))}
                                </AddGrupoSelect>
                            )}

                            {gruposDoUsuario.length === 0 && disponiveis.length === 0 && "—"}
                        </GruposCell>
                    );
                },
            },
        ],
        [getGruposDisponiveis, handleAddGrupo, handleRemoveGrupo, managedModulos, savingId]
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <FilterInput
                    placeholder="Pesquisar por nome, usuário, e-mail ou grupo..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.id}
                        isLoading={loading}
                        loadingText="Carregando usuários..."
                        emptyText="NENHUM USUÁRIO ENCONTRADO"
                        initialSort={{ key: "usuario", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                    />
                </TableFlexFill>
            </TableShell>

            {isModalOpen(MODAL_GERENCIAR_GRUPOS_ID) && (
                <ModalGerenciarGrupos onSaved={fetchData} />
            )}

            {isModalOpen(MODAL_NOVO_GRUPO_ID) && (
                <ModalNovoGrupo onSaved={fetchData} />
            )}
        </PageContent>
    );
};

export default GestaoAcessosV2;
